import { Fragment, useState } from "react";
import { cn } from "@/lib/utils";
import { KEYWORD_COLUMN_TOOLTIPS as TT } from "./keywordColumnTooltips";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import { KeywordMasterListAction } from "@/constants";
import { useDeleteKeywordTheme, keywordThemesQueryKey } from "@/resources/keyword-theme";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { keywordApi } from "@/resources/keyword";
import { useAppData } from "@/contexts/AppDataContext";
import queryClient from "@/query-client";
import { toast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { avg, sum, toggleSetItem, getGroupSelectionState, DeleteThemeDialog, RankBadge, GroupTrendSummary } from "./KeywordThemeShared";
import { ThemeHeaderRow } from "./ThemeHeaderRow";
import { KeywordRow } from "./KeywordRow";
import { SortHeader } from "./SortHeader";
import { useThemeSort } from "./useThemeSort";
import { useKeywordColumnResize } from "./useResizableColumn";
import type { KeywordTheme, KeywordThemeKeyword } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";
import { HeroRankColorKey } from "@/components/shared/HeroRankColorKey";
import { SerpMatchRollupValue } from "./SerpMatchCell";
import { HERO_RANK_BAND_CLASSES } from "@/components/keyword-landscape-output/RankingCell";

interface ThemeGroupedViewProps {
  productId: number;
  heroAsin: string;
  themes: KeywordTheme[];
  masterKeywords: Keyword[];
  keywordMap: Map<number, Keyword>;
  isLoading: boolean;
  selectedKeywords: Set<number>;
  onSelectKeyword: (keywordId: number, selected: boolean) => void;
  onOpenTheme: (theme: KeywordTheme) => void;
  onAddKeyword: (theme: KeywordTheme) => void;
  onUpdateMasterList: (keywordIds: number[], action: KeywordMasterListAction) => void;
  onDeleteKeyword: (keywordId: number) => void;
  onOpenLandscape?: (landscapeId: number) => void;
  onOpenAddTheme?: (keywordId?: number) => void;
  readOnly?: boolean;
}

export function ThemeGroupedView({
  productId,
  heroAsin,
  themes,
  masterKeywords,
  keywordMap,
  isLoading,
  selectedKeywords,
  onSelectKeyword,
  onOpenTheme,
  onAddKeyword,
  onUpdateMasterList,
  onDeleteKeyword,
  onOpenLandscape,
  onOpenAddTheme,
  readOnly,
}: ThemeGroupedViewProps) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [confirmDeleteTheme, setConfirmDeleteTheme] = useState<KeywordTheme | null>(null);
  const { selectedBrandId } = useAppData();
  const { mutateAsync: deleteTheme } = useDeleteKeywordTheme();
  const { mutate: addRankTrackerKeywords } = rankTrackerApi.useAddRankTrackerKeywords();
  const { mutate: deleteRankTrackerKeyword } = rankTrackerApi.useDeleteRankTrackerKeyword();
  const { sortField, sortDir, handleSort, sortedThemes, sortThemeKeywords, sortedUnthemed } =
    useThemeSort(themes, masterKeywords, keywordMap);

  const toggle = (id: number) => setCollapsed((prev) => toggleSetItem(prev, id));

  // Takes a Keyword, like its sibling below and like the identical handler in
  // KeywordBankTable/index.tsx. It used to be typed `(keyword: string)` while KeywordRow calls it
  // with the row's whole Keyword object, so the object was posted as the keyword phrase and the
  // toast read `"[object Object]" is now tracked.` — two TS2322s that were reporting a real defect.
  const onAddToRankTracker = (keyword: Keyword) => {
    if (!selectedBrandId) return;
    addRankTrackerKeywords(
      {
        brandId: selectedBrandId,
        heroAsin,
        keywords: [keyword.search],
        source: "manual",
        // Target the row that was clicked -- see AddRankTrackerKeywordsParams.
        keywordIds: [keyword.id],
      },
      {
        onSuccess: () => {
          toast({ title: "Added to Rank Tracker", description: `"${keyword.search}" is now tracked.` });
          queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
        },
      },
    );
  };

  const onRemoveFromRankTracker = (keyword: Keyword) => {
    if (!selectedBrandId) return;
    deleteRankTrackerKeyword(
      { id: keyword.id, brandId: selectedBrandId },
      {
        onSuccess: () => {
          toast({ title: "Removed from Rank Tracker", description: `"${keyword.search}" is no longer tracked.` });
          queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
        },
      },
    );
  };

  const handleDeleteTheme = async (theme: KeywordTheme) => {
    setConfirmDeleteTheme(null);
    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    queryClient.setQueryData<KeywordTheme[]>(queryKey, (old) => old?.filter((t) => t.id !== theme.id) ?? old);
    try {
      await deleteTheme({ productId, themeId: theme.id });
      toast({ title: `Theme "${theme.name}" deleted` });
    } catch {
      queryClient.setQueryData(queryKey, previous);
    }
  };

  // AUT-143/144: same drag-to-resize as the All Keywords tab (KeywordBankTable).
  const { stickyStyle, keywordTextWidth, onResizeStart, onResizeMove, onResizeEnd } =
    useKeywordColumnResize(readOnly ?? false);
  const stickyGrid = readOnly ? "grid grid-cols-[28px_minmax(0,1fr)] items-center" : "grid grid-cols-[40px_28px_minmax(0,1fr)] items-center";

  const filteredKeywordIds = new Set(masterKeywords.map((k) => k.id));

  const unthemedAvgRank = avg(sortedUnthemed.map((k) => k.hero_rank));
  const unthemedTotalVol = sum(sortedUnthemed.map((k) => k.search_volume));
  const unthemedAvgKps = avg(sortedUnthemed.map((k) => k.kps));
  const unthemedAvgRel = avg(sortedUnthemed.map((k) => k.hero_relevance));
  // AUT-496: mirrors ThemeHeaderRow's own average -- `avg` skips nulls, so this is the mean of the
  // unthemed keywords that have a value and a dash when none do.
  const unthemedAvgSerpMatch = avg(sortedUnthemed.map((k) => k.serp_match_pct));
  const unthemedCollapsed = collapsed.has(-1);
  const unthemedIds = sortedUnthemed.map((k) => k.id);
  const unthemedSelection = getGroupSelectionState(unthemedIds, selectedKeywords);

  const allVisibleIds = masterKeywords.map((k) => k.id);
  const allVisibleSelection = getGroupSelectionState(allVisibleIds, selectedKeywords);
  const selectAllVisible = (checked: boolean) =>
    allVisibleIds.forEach((id) => onSelectKeyword(id, checked));

  if (isLoading) {
    return (
      <div className="rounded-md border-t border-border border-x-0 border-b-0 bg-card">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full mb-px" />)}
      </div>
    );
  }

  return (
    <>
      <div className="w-full min-w-0 rounded-md border-t border-border border-x-0 border-b-0 bg-card">
        <Table style={{ minWidth: (readOnly ? 1124 : 1194) + stickyStyle.width }}>
          <TableHeader>
            <TableRow>
              <TableHead sticky style={stickyStyle} className="py-0 px-0">
                <div className={stickyGrid}>
                  {!readOnly && (
                    <span className="flex items-center justify-center">
                      <Checkbox
                        checked={allVisibleSelection.allSelected ? true : allVisibleSelection.someSelected ? "indeterminate" : false}
                        onCheckedChange={(checked) => selectAllVisible(!!checked)}
                        aria-label="Select all keywords in the Master List"
                      />
                    </span>
                  )}
                  <span />
                  <span className="py-2.5 px-3">
                    <SortHeader
                      label="Keywords"
                      field="keyword"
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      className="justify-start whitespace-nowrap"
                    />
                  </span>
                </div>
                {/* AUT-143/144: drag to resize the keyword column. */}
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize keyword column"
                  onPointerDown={onResizeStart}
                  onPointerMove={onResizeMove}
                  onPointerUp={onResizeEnd}
                  onPointerCancel={onResizeEnd}
                  className={cn(
                    "!absolute right-0 top-0 bottom-0 w-2 -mr-1 cursor-col-resize touch-none select-none z-10",
                    "before:content-[''] before:absolute before:right-[3px] before:top-2 before:bottom-2 before:w-px before:bg-border",
                    "hover:before:right-0 hover:before:top-0 hover:before:bottom-0 hover:before:w-full hover:before:bg-primary/40",
                    "active:before:bg-primary/60 before:transition-all",
                  )}
                />
              </TableHead>
              <TableHead className="w-[90px] py-2.5 px-2">
                {/* AUT-442: the rank colour key moved here from the Keywords header -- it
                    describes this column, under this column's own definition. */}
                <SortHeader
                  label="Hero Rank"
                  field="rank"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  className="whitespace-nowrap"
                  tooltip={
                    <>
                      <p>{TT.heroRank}</p>
                      <HeroRankColorKey swatches={HERO_RANK_BAND_CLASSES} />
                    </>
                  }
                />
              </TableHead>
              <TableHead className="w-[64px] py-2.5 px-2">
                <SortHeader label="KPS" field="kps" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.kps} />
              </TableHead>
              <TableHead className="w-[110px] py-2.5 px-2">
                <SortHeader label="KW Sales" field="keywordSales" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.kwSales} />
              </TableHead>
              <TableHead className="w-[110px] py-2.5 px-2">
                <SortHeader label="Search Vol." field="volume" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.searchVol} />
              </TableHead>
              <TableHead className="w-[110px] py-2.5 px-2">
                <SortHeader label="ASIN Density" field="asinDensity" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.asinDensity} />
              </TableHead>
              <TableHead className="w-[120px] py-2.5 px-2">
                <SortHeader label="Competition" field="competition" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.competition} />
              </TableHead>
              <TableHead className="w-[60px] py-2.5 px-2">
                <SortHeader label="Rel." field="rel" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.rel} />
              </TableHead>
              {/* AUT-496: KeywordRow renders the bank variant of KeywordMetricCells here, and
                  ThemeHeaderRow renders its own cell to match, so the header has to exist or every
                  column right of Rel. sits under the wrong one. */}
              <TableHead className="w-[90px] py-2.5 px-2">
                <SortHeader label="SERP Match" field="serpMatch" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.serpMatch} />
              </TableHead>
              <TableHead className="w-[84px] min-w-[84px] py-2.5 px-1">
                <SortHeader label="Trend" field="trend" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="whitespace-nowrap" tooltip={TT.trend} />
              </TableHead>
              <TableHead className="w-[115px] py-2.5 px-2 font-semibold text-sm text-center whitespace-nowrap">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      className="cursor-help border-b border-dotted border-muted-foreground"
                      onClick={(e) => e.preventDefault()}
                    >
                      Theme
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px]">
                      {TT.theme}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="w-[123px] py-2.5 px-2 font-semibold text-sm text-center whitespace-nowrap">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      className="cursor-help border-b border-dotted border-muted-foreground"
                      onClick={(e) => e.preventDefault()}
                    >
                      Utilization
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px]">
                      {TT.utilization}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="w-[138px] py-2.5 px-2 font-semibold text-sm text-center whitespace-nowrap">
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      className="cursor-help border-b border-dotted border-muted-foreground"
                      onClick={(e) => e.preventDefault()}
                    >
                      Source
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px]">
                      {TT.source}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              {!readOnly && <TableHead className="w-[70px] py-2.5 px-2 font-semibold text-sm text-center whitespace-nowrap">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedThemes.map((theme) => {
              const isCollapsed = collapsed.has(theme.id);
              const themeKeywords = sortThemeKeywords(
                theme.keywords
                  .map((tk) => ({ tk, kw: keywordMap.get(tk.keyword_id) }))
                  .filter(
                    (x): x is { tk: KeywordThemeKeyword; kw: Keyword } =>
                      x.kw != null && filteredKeywordIds.has(x.kw.id),
                  ),
              );
              if (themeKeywords.length === 0) return null;
              const themeKeywordIds = themeKeywords.map((x) => x.kw.id);
              const themeSelection = getGroupSelectionState(themeKeywordIds, selectedKeywords);

              return (
                <Fragment key={theme.id}>
                  <ThemeHeaderRow
                    theme={theme}
                    themeKeywords={themeKeywords.map((x) => x.kw)}
                    isCollapsed={isCollapsed}
                    onToggle={() => toggle(theme.id)}
                    onOpenTheme={() => onOpenTheme(theme)}
                    onAddKeyword={() => onAddKeyword(theme)}
                    onDelete={() => setConfirmDeleteTheme(theme)}
                    readOnly={readOnly}
                    allSelected={themeSelection.allSelected}
                    someSelected={themeSelection.someSelected}
                    onToggleSelectAll={(checked) => themeKeywordIds.forEach((id) => onSelectKeyword(id, checked))}
                  />
                  {!isCollapsed && themeKeywords.map(({ tk, kw }) => (
                    <KeywordRow
                      key={kw.id}
                      keyword={kw}
                      themeKeyword={tk}
                      isInTheme
                      themeId={theme.id}
                      productId={productId}
                      themes={themes}
                      selected={selectedKeywords.has(kw.id)}
                      onSelectKeyword={onSelectKeyword}
                      onUpdateMasterList={onUpdateMasterList}
                      onDeleteKeyword={onDeleteKeyword}
                      onOpenLandscape={onOpenLandscape}
                      onOpenAddTheme={onOpenAddTheme}
                      onAddToRankTracker={onAddToRankTracker}
                      onRemoveFromRankTracker={onRemoveFromRankTracker}
                      readOnly={readOnly}
                      indent
                      stickyStyle={stickyStyle}
                      stickyGrid={stickyGrid}
                      keywordTextWidth={keywordTextWidth}
                    />
                  ))}
                </Fragment>
              );
            })}

            {sortedUnthemed.length > 0 && (
              <>
                <TableRow
                  className="bg-muted/20 hover:bg-muted/40 cursor-pointer border-b border-border"
                  onClick={() => toggle(-1)}
                >
                  <TableCell sticky style={stickyStyle} className="py-0 px-0">
                    <div className={stickyGrid}>
                      {!readOnly && (
                        <span className="py-2 px-2 flex items-center justify-center">
                          <Checkbox
                            checked={unthemedSelection.allSelected ? true : unthemedSelection.someSelected ? "indeterminate" : false}
                            onCheckedChange={(checked) => unthemedIds.forEach((id) => onSelectKeyword(id, !!checked))}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Select all unthemed keywords"
                          />
                        </span>
                      )}
                      <span className="py-2 flex items-center justify-center text-muted-foreground">
                        {unthemedCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                      <span className="py-2 px-3">
                        <span className="font-semibold text-sm text-muted-foreground block">Unthemed</span>
                        <span className="text-xs text-muted-foreground block">{sortedUnthemed.length} keywords</span>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-[90px] py-2 px-2 text-center">
                    {unthemedAvgRank != null ? (
                      <div className="inline-flex items-center justify-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
                        <RankBadge rank={unthemedAvgRank} />
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="w-[64px] py-2 px-2 text-center tabular-nums font-semibold">
                    {unthemedAvgKps != null ? (
                      <div className="inline-flex items-center justify-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
                        <span>{unthemedAvgKps}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="w-[110px]" />
                  <TableCell className="w-[110px] py-2 px-2 text-center tabular-nums font-semibold">
                    {unthemedTotalVol != null ? (
                      <div className="inline-flex items-center justify-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Agr</span>
                        <span>{formatMetricNumber(unthemedTotalVol)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="w-[110px]" />
                  <TableCell className="w-[120px]" />
                  <TableCell className="w-[60px] py-2 px-2 text-center tabular-nums font-semibold">
                    {unthemedAvgRel != null ? (
                      <div className="inline-flex items-center justify-center gap-1">
                        <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
                        <span>{unthemedAvgRel}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {/* AUT-496: the Unthemed group is its own markup, not ThemeHeaderRow, so it
                      needs the cell too -- the header/cell count test caught this one. */}
                  <TableCell className="w-[90px] py-2 px-2 text-center tabular-nums font-semibold">
                    <SerpMatchRollupValue pct={unthemedAvgSerpMatch} />
                  </TableCell>
                  <TableCell className="w-[84px] min-w-[84px] py-2 px-1">
                    <GroupTrendSummary keywords={sortedUnthemed} />
                  </TableCell>
                  <TableCell className="w-[115px]" />
                  <TableCell className="w-[123px]" />
                  <TableCell className="w-[138px]" />
                  {!readOnly && <TableCell className="w-[70px]" />}
                </TableRow>

                {!unthemedCollapsed && sortedUnthemed.map((kw) => (
                  <KeywordRow
                    key={kw.id}
                    keyword={kw}
                    isInTheme={false}
                    productId={productId}
                    themes={themes}
                    selected={selectedKeywords.has(kw.id)}
                    onSelectKeyword={onSelectKeyword}
                    onUpdateMasterList={onUpdateMasterList}
                    onDeleteKeyword={onDeleteKeyword}
                    onAddToRankTracker={onAddToRankTracker}
                    onRemoveFromRankTracker={onRemoveFromRankTracker}
                    onOpenLandscape={onOpenLandscape}
                    onOpenAddTheme={onOpenAddTheme}
                    readOnly={readOnly}
                    indent
                    stickyStyle={stickyStyle}
                    stickyGrid={stickyGrid}
                    keywordTextWidth={keywordTextWidth}
                  />
                ))}
              </>
            )}

            {masterKeywords.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={readOnly ? 12 : 13}
                  className="p-8 text-center text-muted-foreground text-sm"
                >
                  No keywords in the master list yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DeleteThemeDialog
        theme={confirmDeleteTheme}
        onConfirm={() => confirmDeleteTheme && handleDeleteTheme(confirmDeleteTheme)}
        onCancel={() => setConfirmDeleteTheme(null)}
      />
    </>
  );
}
